import {
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  Flex,
  Grid,
  GridItem,
  Heading,
  HStack,
  IconButton,
  Image,
  Progress,
  Select,
  Spinner,
  Text,
  VStack,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { AddIcon, MinusIcon } from "@chakra-ui/icons";
import { useTranslation } from "react-i18next";
import { createBooking } from "../../services/bookings";
import { getCategories } from "../../services/vehicles";
import { RESERVATION_TYPES, ROLES } from "../../constants";
import { useAuth } from "../../contexts/AuthContext";
import _ from "lodash";
import TripCard from "./TripCard";
import TripChangeModal from "./TripChangeModal";
import TourRouteChips from "./TourRouteChips";
import TourItinerarySummary from "./TourItinerarySummary";
// eslint-disable-next-line no-unused-vars
import ChooseStayMenu from "./ChooseStayMenu";
import { useState, useEffect, useRef } from "react";
import ReservationTabs from "../../components/reservation-tabs/ReservationTabs";
import SeoHead from "../../components/seo/SeoHead";
import {
  //pickUpTimeInputOptions,
  SARAJEVO_INTL_AIRPORT,
  APARTMENT_PICKUP_LOCATION,
  VEHICLE_PICKUP_LOCATIONS,
} from "../../constants";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTrashCan,
  faBackward,
  faLock,
  faCircleCheck,
} from "@fortawesome/free-solid-svg-icons";
import { MdAirlineSeatReclineExtra } from "react-icons/md";
import { getCityDualDisplayName } from "../../utils/tripDisplay";
import { getTransportationTypeName } from "../../utils/vehicleDisplay";
import {
  applyTripToSlot,
  applyFreeDayToSlot,
  buildPreservedTripsByDate,
  FREE_DAY_SELECTION_ID,
  getGlobalDayNumber,
  getTripOptionsForDay,
  isFreeDaySelectionId,
  isFreeDayTrip,
  resolveTripForDate,
  swapTripsBetweenSlots,
} from "../../utils/tourTripOptions";
import "./TourBuilder.scss";

const CITIES = ["Sarajevo", "Mostar", "Jajce", "Bihac"];

const TOUR_CITY_IMAGES = {
  Sarajevo: "/sarajevo-2.jpg",
  Mostar: "/mostar-card.jpg",
  Jajce: "/jajce.jpg",
  Bihac: "/bihac.jpg",
};

const PICKUP_LABELS = {
  [SARAJEVO_INTL_AIRPORT]: "pickupAirport",
  [APARTMENT_PICKUP_LOCATION]: "pickupApartment",
};

const formattedDateString = (dateString, locale = "en-GB") => {
  return new Date(dateString).toLocaleDateString(locale, {
    timeZone: "Europe/Sarajevo",
    day: "2-digit",
    month: "short",
  });
};

const getDatesArray = (startDate, endDate) => {
  const fmt = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Sarajevo",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  let dates = [];
  let currentDate = new Date(startDate + "T12:00:00Z");
  const end = new Date(endDate + "T12:00:00Z");
  while (currentDate <= end) {
    dates.push(fmt.format(currentDate));
    currentDate = new Date(currentDate.getTime() + 86400000);
  }
  return dates;
};

const getTourName = (tour, tourDuration, t) => {
  const cities = [...new Set(tour.map((group) => group.destination))];
  return `${cities.join(", ").replace(/, ([^,]*)$/, " & $1")} - ${t(
    "tours.builder.tourNameDays",
    { duration: tourDuration }
  )}`;
};

// eslint-disable-next-line no-unused-vars
const validateTourStay = (tourStays, city, duration, index) => {
  // Must stay at least 1 day in Sarajevo
  if (index === 0 && (city !== "Sarajevo" || duration < 1)) return false;
  // Last day must be in Sarajevo
  if (index === tourStays.length - 1 && city !== "Sarajevo") return false;
  // At least 2 consecutive days in Bihac
  if (city === "Bihac" && duration < 2) return false;
  // No return to the same city
  if (index > 0 && !!tourStays.find((stay) => stay.city === city)) return false;

  return true;
};

const rearrangeStayDurations = (tourStays, totalDuration) => {
  const staysCount = Object.keys(tourStays).length;
  const daysToDistributePerStay = Math.floor((totalDuration - 1) / (staysCount - 1));

  return tourStays
      .map((stay, index) => {
        let duration;

        // Custom overrides za turu od 7 i 8 dana
        if (totalDuration === 7 && index === 0) {
          duration = 2;
        } else if (totalDuration === 8 && index === 4) {
          duration = 2;
        } else if (index === staysCount - 1) {
          duration = 1;
        } else if (index === staysCount - 2) {
          duration =
              totalDuration -
              1 -
              daysToDistributePerStay * (staysCount - 2) -
              (daysToDistributePerStay === 1 ? 1 : 0);
        } else if (stay.city === "Bihac" && daysToDistributePerStay === 1) {
          duration = 2;
        } else {
          duration = daysToDistributePerStay;
        }

        return {
          ...stay,
          duration,
        };
      })
      .filter((stay) => stay.duration > 0);
};

const groupTourDaysIntoArray = (tour) => {
  const tourArray = Object.keys(tour).map((key) => {
    return { date: key, trip: tour[key] };
  });

  return tourArray.reduce((acc, tourDay, index) => {
    acc = acc ?? [];
    const { date, trip } = tourDay;

    if (
      index === 0 ||
      trip.destination !== tourArray[index - 1].trip.destination
    ) {
      acc.push({
        startDate: date,
        endDate: tourArray[index + 1]?.date || date,
        destination: trip.destination,
        stay: {
          id: null,
          name: "",
        },
        trips: [
          {
            ...trip,
            date: date,
          },
        ],
      });
    } else {
      acc[acc.length - 1].endDate = tourArray[index + 1]?.date || date;
      acc[acc.length - 1].trips.push({
        ...trip,
        date: date,
      });
    }

    return acc;
  }, []);
};

const getOtherStaysDuration = (tourStays, index) =>
  tourStays
    .filter((_, i) => i !== index)
    .reduce((acc, stay) => acc + stay.duration, 0);

const canDecrementStayDays = (stay) =>
  stay.duration > 1 && !(stay.city === "Bihac" && stay.duration <= 2);

const canIncrementStayDays = (stay, tourStays, index, tourDuration) =>
  stay.duration + 1 + getOtherStaysDuration(tourStays, index) <= tourDuration;

const isStayDaysLocked = (stay, tourStays, index, tourDuration) =>
  !canDecrementStayDays(stay) &&
  !canIncrementStayDays(stay, tourStays, index, tourDuration);

const makeTourFromStays = (
  tourStays,
  startDate,
  endDate,
  preservedByDate = {}
) => {
  let tour = {};
  const cities = [];
  tourStays.forEach((stay) =>
    _.times(stay.duration, () => cities.push(stay.city))
  );
  const tourTemplate = require("./tourTemplate.json");

  getDatesArray(startDate, endDate).forEach((date, index) => {
    const startingPoint = cities[index - 1] || "Sarajevo";
    const destination = cities[index] || "Sarajevo";
    const usedTripsForDestination = Object.values(tour).filter(
      (trip) => trip.destination === destination
    );
    const recommendedTrip = resolveTripForDate(
      tourTemplate,
      { startingPoint, destination, date },
      usedTripsForDestination,
      preservedByDate
    );

    tour[date] = {
      startingPoint: startingPoint,
      destination: destination,
      id: recommendedTrip?.id,
      description: recommendedTrip?.description || "",
      attractions: recommendedTrip?.attractions || [],
      stayId: null,
      submitted: false,
    };
  });

  return groupTourDaysIntoArray(tour);
};

const TourBuilder = () => {
  const [searchParams] = useSearchParams();
  const tourStartDate = searchParams.get("start_date");
  const tourEndDate = searchParams.get("end_date");
  const adults = parseInt(searchParams.get("adults"));
  const children = searchParams.getAll("children");
  const startDate = searchParams.get("start_date");
  const endDate = searchParams.get("end_date");
  // const rooms = parseInt(searchParams.get("rooms")); // re-enable when ChooseStayMenu uses room count

  //const [pickUpTime, setPickUpTime] = useState("08:00");
  const [pickupLocation, setPickupLocation] = useState(SARAJEVO_INTL_AIRPORT);

  const tourTemplate = require("./tourTemplate.json");
  const tourDuration = getDatesArray(tourStartDate, tourEndDate).length - 1;
  const duration = tourDuration - 2;
  const initialTourStays = rearrangeStayDurations(
    [
      {
        city: "Sarajevo",
        duration: Math.floor(duration / CITIES.length) + 1,
        trips: [],
        stayId: null,
      },
      {
        city: "Mostar",
        duration: Math.floor(duration / CITIES.length),
        trips: [],
        stayId: null,
      },
      {
        city: "Jajce",
        duration: Math.floor(duration / CITIES.length),
        trips: [],
        stayId: null,
      },
      {
        city: "Bihac",
        duration: Math.floor(duration / CITIES.length),
        trips: [],
        stayId: null,
      },
      {
        city: "Sarajevo",
        duration: 1,
        trips: [],
        stayId: null,
      },
    ],
    tourDuration
  );
  //const [allDaysPicked, setAllDaysPicked] = useState(true);
  const [tourStays, setTourStays] = useState(initialTourStays);
  const [tour, setTour] = useState(null);
  const [selectedTripId, setSelectedTripId] = useState(null);
  const [currentTripData, setCurrentTripData] = useState({
    selectedStayIndex: null,
    selectedTripIndex: null,
  });
  const changeRouteCancelRef = useRef(null);
  const itineraryTopRef = useRef(null);
  const scrollToItineraryRef = useRef(false);

  useEffect(() => {
    setTourStays(initialTourStays);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, adults /*, children*/]);

  useEffect(() => {
    if (!tour || !scrollToItineraryRef.current) return;

    scrollToItineraryRef.current = false;
    requestAnimationFrame(() => {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
      itineraryTopRef.current?.scrollIntoView({ behavior: "auto", block: "start" });
    });
  }, [tour]);

  const {
    isOpen: isTripChangeModalOpen,
    onOpen: onTripChangeModalOpen,
    onClose: onTripChangeModalClose,
  } = useDisclosure();
  const {
    isOpen: isChangeRouteOpen,
    onOpen: onChangeRouteOpen,
    onClose: onChangeRouteClose,
  } = useDisclosure();

  const toast = useToast();
  const { t, i18n } = useTranslation(["home", "common", "vehicle", "seo"]);
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const canBook = ![ROLES.ADMINISTRATOR, ROLES.DOMESTIC_PARTNER].includes(currentUser?.type);
  const dateLocale = i18n.language === "ar" ? "ar-SA" : "en-GB";
  const isRtl = i18n.language === "ar";
  const allocatedDays = tourStays.reduce(
    (total, stay) => total + (stay.duration || 0),
    0
  );
  const daysComplete = allocatedDays === tourDuration;

  const vehicleCategoriesQuery = useQuery(
    ["vehicleCategories", tourStartDate, tourEndDate, adults, children],
    () => getCategories(),
    {
      refetchOnWindowFocus: false,
      select: (payload) => payload.data,
      onSuccess: (data) => {
        var childrenPassengers = 0;
        if (children.length !== 0) {
          childrenPassengers = children.length;
        }
        var min = adults + childrenPassengers;
        for (let category of data) {
          if (parseInt(category.seats) >= adults + childrenPassengers) {
            min = category;
            break;
          }
        }
        setSelectedTransportationKey(min);
      },
    }
  );

  // eslint-disable-next-line no-unused-vars
  const fullStayName = (stay) => {
    return `${
      stay.accommodationType === "hotel" ? stay.accommodationName + " - " : ""
    }${stay.name}`;
  };

  const staysCount = Object.keys(tourStays).length;
  const uniqueCitiesInTour = [...new Set(tourStays.map((stay) => stay.city))];
  const totalStayDuration = tourStays.reduce(
    (acc, stay) => acc + stay.duration,
    0
  );
  const stayPeriods = [];
  const stayDates = getDatesArray(tourStartDate, tourEndDate);
  let dateOffset = 0;
  tourStays.forEach((stay, index) => {
    stayPeriods.push({
      startDate: stayDates[dateOffset],
      endDate: stayDates[dateOffset + stay.duration],
    });
    dateOffset += stay.duration;
  });

  const createBookingMutation = useMutation(
    () => {
      /*const bookedStays = tour
        .filter((tourStay) => tourStay.stay.id !== null)
        .map((tourStay) => {
          return {
            id: tourStay.stay.id,
            type: "AccommodationUnit",
            quantity: tourStay.stay.quantity,
            startDate: tourStay.startDate,
            endDate: tourStay.endDate,
            // endDate: new Date(trip.date).toISOString().split("T")[0],
          };
        });
        */
      const bookedStays = tour.flatMap((tourStay) => {
        const stays = Array.isArray(tourStay.stay) ? tourStay.stay : [];
        return stays
          .filter((stay) => stay.id !== null) // Filter stays with a non-null id
          .map((stay) => ({
            id: stay.id,
            type: "AccommodationUnit",
            quantity: stay.quantity,
            startDate: tourStay.startDate,
            endDate: tourStay.endDate,
          }));
      });

      const tourData = tour.map((tourStay) => {
        return {
          city: tourStay.destination,
          startDate: tourStay.startDate,
          endDate: tourStay.endDate,
          //stay: tourStay.stay.name,
          //stay: tourStay.stay.map((stay) => stay.name).join(", "),
          stay: Array.isArray(tourStay.stay)
            ? tourStay.stay.map((stay) => `${stay.quantity}x ${stay.name}`).join(", ")
            : "No stays", // Handle non-array cases
          trips: tourStay.trips.map((trip) => {
            return {
              description: trip.description,
              attractions: trip.attractions,
            };
          }),
        };
      });

      const bookingData = {
        bookingType: RESERVATION_TYPES.TOUR.toLowerCase(),
        startDate: tourStartDate,
        endDate: tourEndDate,
        adults: parseInt(adults),
        children: children.length,
        tourData: tourData,
        pickupLocation: pickupLocation,
        selectedVehicleCategory: selectedTransportationKey.key,
        driverIncluded: true,
        bookables: [
          // { id: null, quantity: 1, type: "Tour", tourData: tour },
          ...bookedStays,
        ],
      };
      return createBooking(bookingData);
    },
    {
      onSuccess: (data) => {
        toast({
          title: t("tours.builder.bookingCreated"),
          description: t("tours.builder.bookingCreatedDesc"),
          status: "success",
          duration: 5000,
          isClosable: true,
        });
        navigate(`/book/${data.data.uuid}`);
      },
      onError: (error) => {
        const isUnavailable = error?.response?.status === 409;
        toast({
          title: t("common:bookingErrorTitle"),
          description: isUnavailable
            ? t("common:bookingDatesUnavailable")
            : t("common:bookingGenericError"),
          status: "error",
          duration: 7000,
          isClosable: true,
          position: "top",
        });
      },
    }
  );

  const bookTour = () => {
    createBookingMutation.mutate();
  };

  const closeTripChangeModal = () => {
    setCurrentTripData({
      selectedStayIndex: null,
      selectedTripIndex: null,
    });
    setSelectedTripId(null);
    onTripChangeModalClose();
  };

  const applyTourWithUndo = (newTour) => {
    const previousTour = tour;
    setTour(newTour);
    toast({
      title: t("tours.builder.activityUpdated"),
      status: "success",
      duration: 8000,
      isClosable: true,
      render: ({ onClose }) => (
        <Box color="white" p={3}>
          <Text fontWeight="semibold">{t("tours.builder.activityUpdated")}</Text>
          <Button
            size="sm"
            mt={2}
            variant="outline"
            colorScheme="whiteAlpha"
            onClick={() => {
              setTour(previousTour);
              onClose();
            }}
          >
            {t("tours.builder.undo")}
          </Button>
        </Box>
      ),
    });
  };

  const handleApplyTripChange = () => {
    if (
      currentTripData.selectedStayIndex == null ||
      currentTripData.selectedTripIndex == null ||
      !selectedTripId
    ) {
      return;
    }

    const currentTrip =
      tour[currentTripData.selectedStayIndex].trips[currentTripData.selectedTripIndex];

    if (isFreeDaySelectionId(selectedTripId)) {
      if (isFreeDayTrip(currentTrip)) {
        closeTripChangeModal();
        return;
      }

      const newTour = applyFreeDayToSlot(
        tour,
        currentTripData.selectedStayIndex,
        currentTripData.selectedTripIndex
      );
      applyTourWithUndo(newTour);
      closeTripChangeModal();
      return;
    }

    const templateTrip = tourTemplate.find((entry) => entry.id === selectedTripId);
    if (!templateTrip) return;

    if (currentTrip?.id === selectedTripId && !isFreeDayTrip(currentTrip)) {
      closeTripChangeModal();
      return;
    }

    const options = getTripOptionsForDay(tour, tourTemplate, {
      stayIndex: currentTripData.selectedStayIndex,
      tripIndex: currentTripData.selectedTripIndex,
    });
    const isUsedElsewhere = options?.used?.some(
      ({ trip }) => trip.id === selectedTripId
    );
    if (isUsedElsewhere) {
      toast({
        title: t("tours.builder.chooseTrip"),
        description: t("tours.builder.alreadyUsedInCity"),
        status: "warning",
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    const newTour = applyTripToSlot(
      tour,
      currentTripData.selectedStayIndex,
      currentTripData.selectedTripIndex,
      templateTrip
    );
    applyTourWithUndo(newTour);
    closeTripChangeModal();
  };

  const handleSwapTrip = (usedOn) => {
    const newTour = swapTripsBetweenSlots(
      tour,
      currentTripData.selectedStayIndex,
      currentTripData.selectedTripIndex,
      usedOn.stayIndex,
      usedOn.tripIndex
    );
    applyTourWithUndo(newTour);
    closeTripChangeModal();
  };

  const handleGeneratePlan = () => {
    const preservedByDate = tour ? buildPreservedTripsByDate(tour) : {};
    scrollToItineraryRef.current = true;
    setTour(makeTourFromStays(tourStays, tourStartDate, tourEndDate, preservedByDate));
  };

  const handleConfirmChangeRoute = () => {
    setTour(null);
    onChangeRouteClose();
  };

  const requiredSeats = adults + children.length;

  const minimalCategory = vehicleCategoriesQuery.data?.find(
    (category) => parseInt(category.seats) >= requiredSeats
  );
  const [selectedTransportationKey, setSelectedTransportationKey] =
    useState(minimalCategory);
  //setSelectedTransportationKey(minimalCategory);

  return (
    <Flex direction="column" className="tour-builder" gap={6} pb={8}>
      <SeoHead
        title={t("seo:tourBuilderTitle")}
        description={t("seo:tourBuilderDescription")}
        path="/tours/builder"
      />
      <ReservationTabs
        selectedTab={RESERVATION_TYPES.TOUR}
        forHomePage={false}
        isFetching={false}
        onSearchButtonClick={() => {}}
      />

      {tour && (
        <Box ref={itineraryTopRef} w="full">
          <Heading size="lg" color="gray.800" fontWeight="700">
            {getTourName(tour, tourDuration, t)}
          </Heading>
          <Text fontSize="sm" color="gray.500" mt={1}>
            {t("tours.builder.tourSummary", {
              adults,
              startDate: formattedDateString(tourStartDate, dateLocale),
              endDate: formattedDateString(tourEndDate, dateLocale),
            })}
          </Text>
        </Box>
      )}

      {!tour && (
        <Card
          w="full"
          maxW="800px"
          alignSelf="flex-start"
          borderRadius="2xl"
          border="1px solid"
          borderColor="gray.100"
          shadow="sm"
        >
          <CardBody p={{ base: 5, md: 8 }}>
            <VStack align="stretch" spacing={6}>
              <Box>
                <Heading size="md" color="gray.800" fontWeight="700" mb={1}>
                  {t("tours.builder.routeTitle")}
                </Heading>
                <Text fontSize="sm" color="gray.500">
                  {t("tours.builder.routeSubtitle")}
                </Text>
              </Box>

              <Box>
                <Flex justify="space-between" align="center" mb={2}>
                  <Text fontSize="sm" fontWeight="semibold" color="gray.700">
                    {t("tours.builder.daysProgress", {
                      allocated: allocatedDays,
                      total: tourDuration,
                    })}
                  </Text>
                  <Text fontSize="xs" color={daysComplete ? "teal.600" : "orange.500"}>
                    {daysComplete
                      ? "✓"
                      : t("tours.builder.daysRemaining", { total: tourDuration })}
                  </Text>
                </Flex>
                <Progress
                  value={(allocatedDays / tourDuration) * 100}
                  size="sm"
                  colorScheme={daysComplete ? "teal" : "orange"}
                  borderRadius="full"
                />
              </Box>

              <HStack spacing={4} fontSize="xs" color="gray.600" flexWrap="wrap">
                <HStack spacing={2}>
                  <Box w="3" h="3" borderRadius="sm" bg="teal.400" />
                  <Text>{t("tours.builder.legendAdjustable")}</Text>
                </HStack>
                <HStack spacing={2}>
                  <Box w="3" h="3" borderRadius="sm" bg="gray.300" />
                  <Text>{t("tours.builder.legendFixed")}</Text>
                </HStack>
              </HStack>

              <Box className="tour-builder__timeline">
                {tourStays.map((stay, index) => {
                  const isCityLocked = index === 0 || index === staysCount - 1;
                  const canDecrement = canDecrementStayDays(stay);
                  const canIncrement = canIncrementStayDays(
                    stay,
                    tourStays,
                    index,
                    tourDuration
                  );
                  const isDaysLocked = isStayDaysLocked(
                    stay,
                    tourStays,
                    index,
                    tourDuration
                  );

                  return (
                    <Box key={index} className="tour-builder__timeline-item">
                      <Box className="tour-builder__timeline-rail">
                        <Box
                          className={`tour-builder__timeline-dot${
                            isDaysLocked ? " tour-builder__timeline-dot--locked" : ""
                          }`}
                        />
                      </Box>
                      <Box className="tour-builder__timeline-content">
                      {index === Object.keys(tourStays).length - 1 &&
                        uniqueCitiesInTour.length < 4 &&
                        totalStayDuration < tourDuration && (
                          <Button
                            alignSelf="flex-start"
                            size="sm"
                            variant="outline"
                            colorScheme="teal"
                            borderStyle="dashed"
                            leftIcon={<AddIcon />}
                            mb={3}
                            onClick={() => {
                              const newTourStays = [...tourStays];
                              newTourStays.splice(staysCount - 1, 0, {
                                city: _.differenceBy(CITIES, uniqueCitiesInTour)[0],
                                duration: 1,
                              });
                              setTourStays(newTourStays);
                            }}
                          >
                            {t("tours.builder.addStay")}
                          </Button>
                        )}
                      <Flex
                        w="full"
                        gap={4}
                        align="center"
                        wrap="wrap"
                        p={4}
                        borderRadius="xl"
                        borderWidth="1px"
                        transition="all 0.2s"
                        {...(isDaysLocked
                          ? {
                              bg: "gray.50",
                              borderColor: "gray.200",
                            }
                          : {
                              bg: "white",
                              borderColor: "teal.200",
                              shadow: "sm",
                            })}
                      >
                        <Badge
                          variant="subtle"
                          colorScheme="gray"
                          borderRadius="full"
                          px={3}
                          py={1}
                          fontSize="xs"
                          whiteSpace="nowrap"
                        >
                          {`${formattedDateString(
                            stayPeriods[index].startDate,
                            dateLocale
                          )} – ${formattedDateString(
                            stayPeriods[index].endDate,
                            dateLocale
                          )}`}
                        </Badge>

                        {isCityLocked ? (
                          <HStack minW={{ base: "full", sm: "180px" }} spacing={2}>
                            <Text fontWeight="700" color="gray.800">
                              {getCityDualDisplayName(stay.city, t, i18n.language)}
                            </Text>
                            <Badge colorScheme="teal" variant="subtle" fontSize="xs">
                              {index === 0
                                ? t("tours.builder.startBadge")
                                : t("tours.builder.endBadge")}
                            </Badge>
                          </HStack>
                        ) : (
                          <Select
                            w={{ base: "full", sm: "200px" }}
                            size="sm"
                            bg="white"
                            borderRadius="lg"
                            value={stay.city}
                            onChange={(selected) => {
                              const selectedCity = selected.target.value;
                              const newTourStays = tourStays.map((s) =>
                                s.city === selectedCity && s.city !== "Sarajevo"
                                  ? { ...s, city: stay.city }
                                  : s
                              );

                              setTourStays(
                                newTourStays.map((s, i) =>
                                  i === index ? { ...s, city: selectedCity } : s
                                )
                              );
                            }}
                          >
                            {CITIES.map((city) => (
                              <option
                                disabled={tourStays
                                  .map((s) => s.city)
                                  .slice(0, index)
                                  .includes(city)}
                                key={city}
                                value={city}
                              >
                                {getCityDualDisplayName(city, t, i18n.language)}
                              </option>
                            ))}
                          </Select>
                        )}

                        {isDaysLocked ? (
                          <HStack spacing={2} ml={{ base: 0, sm: "auto" }}>
                            <Badge
                              colorScheme="gray"
                              variant="subtle"
                              fontSize="sm"
                              px={3}
                              py={1}
                              borderRadius="full"
                            >
                              {stay.duration}{" "}
                              {stay.duration === 1
                                ? t("tours.builder.day")
                                : t("tours.builder.days")}
                            </Badge>
                            <HStack spacing={1} color="gray.400">
                              <FontAwesomeIcon icon={faLock} size="xs" />
                              <Text fontSize="xs">{t("tours.builder.fixed")}</Text>
                            </HStack>
                          </HStack>
                        ) : (
                          <HStack
                            spacing={1}
                            bg="gray.50"
                            px={2}
                            py={1}
                            borderRadius="full"
                            borderWidth="1px"
                            borderColor="gray.200"
                            ml={{ base: 0, sm: "auto" }}
                          >
                            <IconButton
                              aria-label="-"
                              icon={<MinusIcon />}
                              size="xs"
                              borderRadius="full"
                              colorScheme="teal"
                              variant="outline"
                              isDisabled={!canDecrement}
                              onClick={() => {
                                setTourStays(
                                  tourStays.map((s, i) =>
                                    i === index
                                      ? { ...s, duration: s.duration - 1 }
                                      : s
                                  )
                                );
                              }}
                            />
                            <Text
                              minW="24px"
                              textAlign="center"
                              fontWeight="bold"
                              color="teal.700"
                              fontSize="sm"
                            >
                              {stay.duration}
                            </Text>
                            <IconButton
                              aria-label="+"
                              icon={<AddIcon />}
                              size="xs"
                              borderRadius="full"
                              colorScheme="teal"
                              variant="outline"
                              isDisabled={!canIncrement}
                              onClick={() => {
                                setTourStays(
                                  tourStays.map((s, i) =>
                                    i === index
                                      ? { ...s, duration: s.duration + 1 }
                                      : s
                                  )
                                );
                              }}
                            />
                            <Text fontSize="xs" color="gray.500" whiteSpace="nowrap" pe={1}>
                              {stay.duration === 1
                                ? t("tours.builder.day")
                                : t("tours.builder.days")}
                            </Text>
                          </HStack>
                        )}

                        {index !== 0 && index !== staysCount - 1 && (
                          <IconButton
                            aria-label={t("tours.builder.deleteStay")}
                            variant="ghost"
                            size="sm"
                            colorScheme="red"
                            icon={<FontAwesomeIcon icon={faTrashCan} />}
                            ml={{ base: 0, sm: "auto" }}
                            onClick={() => {
                              setTourStays(tourStays.filter((_, i) => i !== index));
                            }}
                          />
                        )}
                      </Flex>
                      </Box>
                    </Box>
                  );
                })}
              </Box>

              <Button
                variant="solid"
                size="lg"
                w="full"
                isDisabled={!daysComplete}
                colorScheme="teal"
                borderRadius="xl"
                onClick={handleGeneratePlan}
              >
                {t("tours.builder.generatePlan")}
              </Button>
            </VStack>
          </CardBody>
        </Card>
      )}
      {tour && (
        <>
          <Button
            variant="ghost"
            colorScheme="teal"
            alignSelf="flex-start"
            leftIcon={
              <FontAwesomeIcon
                icon={faBackward}
                style={{ transform: isRtl ? "scaleX(-1)" : undefined }}
              />
            }
            onClick={onChangeRouteOpen}
          >
            {t("tours.builder.changeTour")}
          </Button>

          <TourRouteChips
            tourStays={tourStays}
            t={t}
            language={i18n.language}
            isRtl={isRtl}
          />

          <Grid
            templateColumns={{ base: "1fr", lg: "1fr 320px" }}
            gap={{ base: 6, lg: 8 }}
            w="full"
            alignItems="start"
          >
            <GridItem minW={0} overflow="visible">
              <VStack spacing={8} align="stretch" w="full" overflow="visible">
                {tour.map((tourGroup, tourStayindex) => {
                  const cityImage = TOUR_CITY_IMAGES[tourGroup.destination];
                  const nightCount = tourGroup.trips.length;

                  return (
                    <Box key={tourStayindex} w="full">
                      <Flex
                        className="tour-builder__city-header"
                        align={{ base: "flex-start", sm: "center" }}
                        gap={4}
                        mb={4}
                        flexDir={{ base: "column", sm: "row" }}
                      >
                        {cityImage && (
                          <Image
                            src={cityImage}
                            alt={tourGroup.destination}
                            boxSize={{ base: "full", sm: "56px" }}
                            maxW={{ base: "100%", sm: "56px" }}
                            h={{ base: "120px", sm: "56px" }}
                            borderRadius="xl"
                            objectFit="cover"
                            flexShrink={0}
                          />
                        )}
                        <Box flex={1}>
                          <Heading size="md" color="gray.800" fontWeight="700">
                            {getCityDualDisplayName(
                              tourGroup.destination,
                              t,
                              i18n.language
                            )}
                          </Heading>
                          <Text fontSize="sm" color="gray.500" mt={1}>
                            {`${formattedDateString(
                              tourGroup.startDate,
                              dateLocale
                            )} – ${formattedDateString(
                              tourGroup.endDate,
                              dateLocale
                            )}`}
                          </Text>
                        </Box>
                        <Badge
                          colorScheme="teal"
                          variant="subtle"
                          borderRadius="full"
                          alignSelf={{ base: "flex-start", sm: "center" }}
                        >
                          {nightCount}{" "}
                          {nightCount === 1
                            ? t("tours.builder.day")
                            : t("tours.builder.days")}
                        </Badge>
                      </Flex>

                      <Box className="tour-builder__itinerary-timeline">
                        {tourGroup.trips.map((trip, index) => (
                          <Box
                            key={`${tourStayindex}-${index}`}
                            className="tour-builder__itinerary-item"
                          >
                            <Box className="tour-builder__timeline-rail">
                              <Box className="tour-builder__timeline-dot" />
                            </Box>
                            <Box className="tour-builder__timeline-content">
                            <TripCard
                              trip={trip}
                              dateLocale={dateLocale}
                              language={i18n.language}
                              dayNumber={getGlobalDayNumber(
                                tour,
                                tourStayindex,
                                index
                              )}
                              onChangeTrip={() => {
                                setCurrentTripData({
                                  selectedTripIndex: index,
                                  selectedStayIndex: tourStayindex,
                                });
                                setSelectedTripId(
                                  isFreeDayTrip(trip)
                                    ? FREE_DAY_SELECTION_ID
                                    : trip.id || null
                                );
                                onTripChangeModalOpen();
                              }}
                            />
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  );
                })}
              </VStack>
            </GridItem>

            <GridItem display={{ base: "none", lg: "block" }}>
              <TourItinerarySummary
                tour={tour}
                t={t}
                language={i18n.language}
                dateLocale={dateLocale}
              />
            </GridItem>
          </Grid>

          <Box display={{ base: "block", lg: "none" }} w="full">
            <TourItinerarySummary
              tour={tour}
              t={t}
              language={i18n.language}
              dateLocale={dateLocale}
            />
          </Box>
        </>
      )}
      <Card
        w="full"
        borderRadius="2xl"
        border="1px solid"
        borderColor="gray.100"
        shadow="sm"
      >
        <CardBody p={{ base: 5, md: 8 }}>
          <VStack alignItems="stretch" gap={6} w="full">
            <Heading size="md" color="gray.800" fontWeight="700">
              {t("tours.builder.transportTitle")}
            </Heading>

            <Flex
              align="center"
              gap={3}
              bg="teal.50"
              borderRadius="xl"
              p={4}
              border="1px solid"
              borderColor="teal.100"
            >
              <Flex
                w={10}
                h={10}
                borderRadius="full"
                bg="teal.500"
                color="white"
                align="center"
                justify="center"
                flexShrink={0}
              >
                <FontAwesomeIcon icon={faCircleCheck} />
              </Flex>
              <Text fontWeight="medium" color="teal.800">
                {t("tours.builder.guideIncluded")}
              </Text>
            </Flex>

            <Box>
              <Text fontWeight="semibold" color="gray.700" mb={3}>
                {t("tours.builder.chooseTransportation")}
              </Text>
              {vehicleCategoriesQuery.isLoading ? (
                <Flex justify="center" py={8}>
                  <Spinner color="teal.500" />
                </Flex>
              ) : (
                <Grid
                  templateColumns={{
                    base: "repeat(1, 1fr)",
                    md: "repeat(2, 1fr)",
                    lg: "repeat(3, 1fr)",
                  }}
                  gap={4}
                >
                  {vehicleCategoriesQuery.data?.map((vehicleCategory, index) => {
                    const categoryDisabled =
                      parseInt(vehicleCategory.seats) < requiredSeats;
                    const isSelected =
                      selectedTransportationKey?.key === vehicleCategory.key;
                    const isRecommended =
                      minimalCategory?.key === vehicleCategory.key;

                    return (
                      <GridItem w="full" key={index}>
                        <Card
                          cursor={categoryDisabled ? "not-allowed" : "pointer"}
                          variant="outline"
                          borderRadius="xl"
                          borderWidth={2}
                          borderColor={isSelected ? "teal.500" : "gray.100"}
                          bg={isSelected ? "teal.50" : "white"}
                          shadow={isSelected ? "md" : "sm"}
                          transition="all 0.2s"
                          position="relative"
                          overflow="hidden"
                          onClick={() => {
                            if (categoryDisabled) return;
                            setSelectedTransportationKey(vehicleCategory);
                          }}
                          _hover={
                            categoryDisabled
                              ? {}
                              : { borderColor: "teal.300", shadow: "md" }
                          }
                        >
                          {categoryDisabled && (
                            <Flex
                              position="absolute"
                              inset={0}
                              bg="whiteAlpha.700"
                              align="center"
                              justify="center"
                              zIndex={1}
                            >
                              <Text
                                fontSize="sm"
                                fontWeight="semibold"
                                color="gray.500"
                              >
                                {t("tours.builder.notEnoughSeats")}
                              </Text>
                            </Flex>
                          )}
                          <CardBody p={5}>
                            <HStack justify="space-between" mb={3}>
                              <Box
                                w={5}
                                h={5}
                                borderRadius="full"
                                borderWidth={2}
                                borderColor={isSelected ? "teal.500" : "gray.300"}
                                bg={isSelected ? "teal.500" : "white"}
                                flexShrink={0}
                              />
                              {isRecommended && !categoryDisabled && (
                                <Badge
                                  colorScheme="teal"
                                  variant="subtle"
                                  borderRadius="full"
                                  fontSize="xs"
                                >
                                  {t("tours.builder.recommended")}
                                </Badge>
                              )}
                            </HStack>
                            <Text
                              fontSize="lg"
                              fontWeight="700"
                              color="gray.800"
                              mb={2}
                            >
                              {getTransportationTypeName(vehicleCategory, t)}
                            </Text>
                            <HStack spacing={2} color="gray.500">
                              <MdAirlineSeatReclineExtra size={18} />
                              <Text fontSize="sm">
                                {t("tours.builder.upToPassengers", {
                                  count: vehicleCategory.seats,
                                })}
                              </Text>
                            </HStack>
                          </CardBody>
                        </Card>
                      </GridItem>
                    );
                  })}
                </Grid>
              )}
            </Box>

            <Box>
              <Text fontWeight="semibold" color="gray.700" mb={3}>
                {t("tours.builder.pickupLocation")}
              </Text>
              <HStack spacing={3} flexWrap="wrap">
                {VEHICLE_PICKUP_LOCATIONS.map((location) => {
                  const labelKey = PICKUP_LABELS[location];
                  const isSelected = pickupLocation === location;
                  return (
                    <Button
                      key={location}
                      variant={isSelected ? "solid" : "outline"}
                      colorScheme="teal"
                      borderRadius="full"
                      size="md"
                      onClick={() => setPickupLocation(location)}
                    >
                      {labelKey
                        ? t(`tours.builder.${labelKey}`)
                        : location}
                    </Button>
                  );
                })}
              </HStack>
            </Box>
          </VStack>
        </CardBody>
      </Card>

      <Box
        className="tour-builder__sticky-book"
        w="full"
        display={{ base: "block", md: "none" }}
      >
        <Button
          colorScheme="teal"
          size="lg"
          w="full"
          borderRadius="xl"
          onClick={() => bookTour()}
          isDisabled={_.isEmpty(tour) || !canBook}
          isLoading={createBookingMutation.isLoading}
          title={!canBook ? t("common:reserveBlockedForRole") : undefined}
        >
          {t("tours.builder.book")}
        </Button>
      </Box>

      <HStack
        w="full"
        justifyContent="flex-end"
        display={{ base: "none", md: "flex" }}
      >
        <Button
          colorScheme="teal"
          size="lg"
          borderRadius="xl"
          px={10}
          onClick={() => bookTour()}
          isDisabled={_.isEmpty(tour) || !canBook}
          isLoading={createBookingMutation.isLoading}
          title={!canBook ? t("common:reserveBlockedForRole") : undefined}
        >
          {t("tours.builder.book")}
        </Button>
      </HStack>

      <TripChangeModal
        isOpen={isTripChangeModalOpen}
        onClose={closeTripChangeModal}
        currentTripData={currentTripData}
        tour={tour}
        template={tourTemplate}
        language={i18n.language}
        dateLocale={dateLocale}
        selectedTripId={selectedTripId}
        onSelectTripId={setSelectedTripId}
        onApply={handleApplyTripChange}
        onSwap={handleSwapTrip}
      />

      <AlertDialog
        isOpen={isChangeRouteOpen}
        leastDestructiveRef={changeRouteCancelRef}
        onClose={onChangeRouteClose}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent mx={4}>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              {t("tours.builder.changeRouteConfirm")}
            </AlertDialogHeader>
            <AlertDialogBody>
              {t("tours.builder.changeRouteConfirmBody")}
            </AlertDialogBody>
            <AlertDialogFooter gap={3}>
              <Button ref={changeRouteCancelRef} onClick={onChangeRouteClose}>
                {t("tours.builder.cancel")}
              </Button>
              <Button colorScheme="red" onClick={handleConfirmChangeRoute}>
                {t("tours.builder.changeRouteConfirmAction")}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Flex>
  );
};

export default TourBuilder;
